import {
	Badge,
	Box,
	Button,
	Card,
	Divider,
	Dropdown,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Table,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	GetFieldValue,
	SubmissionError,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { XHRLink } from "@sembark-travel/ui-router"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useState } from "react"
import useSWR from "swr"
import { useAuthUser } from "../Auth"
import { Email, PhoneNumber } from "../Contacts"
import {
	FileInfo,
	FilePreview,
	TouristXHR,
	TTourist,
	useTourist,
} from "../Tourists"
import { genrateGuestFormLink, ITrip } from "./store"
import * as Validator from "yup"
import { DocumentFileInputField } from "../DocTypes"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

export function TripGuests({
	trip,
}: {
	trip: ITrip
	disabledSharing?: boolean
}) {
	const { user } = useAuthUser()
	const canViewTripVoucher = Boolean(user?.tenant?.feature_flags?.trip_voucher)
	const [selectedTourist, setSelectedTourist] = useState<TTourist | undefined>(
		undefined
	)
	const { tourists } = useTourist({ tripId: trip.id })
	if (!canViewTripVoucher) return null
	return (
		<Box bgColor="default" position="relative" key="tourist-documents">
			<Tabs collapseBelow="md">
				<TabsList>
					<TabItem
						key="all"
						active={!selectedTourist}
						onClick={() => setSelectedTourist(undefined)}
					>
						All Guests
					</TabItem>
					{tourists && tourists.length > 0
						? tourists.map((tourist) => {
								const { id, name, is_primary } = tourist
								return (
									<TabItem
										key={id}
										active={selectedTourist?.id === id}
										onClick={() => setSelectedTourist(tourist)}
									>
										<Inline gap="4">
											<Text>{name}</Text>
											{is_primary ? (
												<Text>
													<Button
														level="secondary"
														status="primary"
														style={{
															paddingLeft: "6px",
															paddingRight: "6px",
															paddingTop: "2px",
															paddingBottom: "2px",
															height: "fit-content",
														}}
													>
														<Icons.FlagSolid />
													</Button>
												</Text>
											) : null}
										</Inline>
									</TabItem>
								)
							})
						: null}
				</TabsList>
				<TabContent>
					{selectedTourist ? (
						<TouristDetailPage tourist={selectedTourist} trip={trip} />
					) : (
						<AllTouristsPage trip={trip} />
					)}
				</TabContent>
			</Tabs>
		</Box>
	)
}

function TouristDetailPage({
	tourist,
	trip,
}: {
	tourist: TTourist
	trip: ITrip
}) {
	const xhr = useXHR()
	const { data, mutate, isValidating, error } = useSWR(
		`/tourists/tourist/${tourist.id}`,
		() => TouristXHR(xhr).getDocumentByTouristId({ touristId: tourist.id })
	)
	if (!data || error) {
		return (
			<Stack padding="4" justifyContent="center" alignItems="center" gap="4">
				<Text>
					{error?.message || "No results found. please try again later."}
				</Text>
				<Button onClick={() => mutate()}>
					<Icons.Refresh spin={isValidating} /> Try Again
				</Button>
			</Stack>
		)
	}
	return (
		<Stack gap="6" padding="4">
			<TouristInfo tourist={data} />
			<TouristDocumentList
				tourist={data}
				revalidate={() => {
					mutate()
				}}
			/>
		</Stack>
	)
}

function TouristInfo({ tourist }: { tourist: TTourist }) {
	const {
		id,
		name,
		age_during_trip,
		nationality,
		email,
		phone_numbers,
		document_tracking,
	} = tourist
	const hasNoDocuments = (document_tracking?.completed_count ?? 0) === 0
	return (
		<Inline
			justifyContent="between"
			bgColor="subtle"
			padding="4"
			rounded="md"
			flexWrap="wrap"
			gap="2"
		>
			<Stack gap="1">
				<Inline gap="2" flexWrap="wrap">
					<Text whiteSpace="nowrap">
						Name:{" "}
						<Text fontWeight="semibold" as="span">
							{name}
						</Text>
					</Text>
					{age_during_trip ? (
						<Text whiteSpace="nowrap">
							| Age:{" "}
							<Text fontWeight="semibold" as="span">
								{age_during_trip}
							</Text>
						</Text>
					) : null}
					{nationality ? (
						<Text whiteSpace="nowrap">
							| Nationality:{" "}
							<Text fontWeight="semibold" as="span">
								{nationality.nationality || <Text color="muted">N/A</Text>}
							</Text>
						</Text>
					) : null}
				</Inline>
				<Inline gap="4">
					{phone_numbers?.length ? (
						<Box>
							<PhoneNumber value={phone_numbers} />
						</Box>
					) : null}
					{email ? (
						<Box>
							<Email value={email} />
						</Box>
					) : null}
				</Inline>
				{document_tracking ? (
					<Inline gap="4" flexWrap="wrap">
						<Text>Total Docs: {document_tracking.total_required}</Text>
						<Text>Uploaded: {document_tracking.completed_count}</Text>
						<Text>Pending: {document_tracking.pending_count}</Text>
					</Inline>
				) : null}
			</Stack>
			<Box>
				{hasNoDocuments ? (
					<Button
						key={id}
						level="secondary"
						status="primary"
						title={`No documents available for download for ${name}`}
						disabled
					>
						<Icons.DocumentDownload /> Download
					</Button>
				) : (
					<Button
						level="secondary"
						status="primary"
						as={XHRLink}
						href={`/tourists/${id}/download-zip`}
						download
						target="_blank"
						title={`Download ${name} all documents`}
					>
						<Icons.DocumentDownload /> Download
					</Button>
				)}
			</Box>
		</Inline>
	)
}
function TouristDocumentList({
	tourist,
	revalidate,
}: {
	tourist: TTourist
	revalidate: () => void
}) {
	const xhr = useXHR()
	const documentList = tourist?.document_tracking?.document_list ?? []
	if (documentList.length === 0) return <EmptyState />

	return (
		<Box>
			<Table
				bordered
				responsive
				striped
				headers={["S No.", "Documents", "Status", ""]}
				alignCols={{ 3: "right" }}
				rows={tourist?.document_tracking?.document_list?.map(
					(
						{
							name,
							media_url: mediaUrl,
							document_type_id: documentTypeId,
							document_id: documentId,
						},
						index
					) => [
						<Text>{index + 1}</Text>,
						<Text>{name}</Text>,
						<Badge success={Boolean(mediaUrl)} warning={!mediaUrl}>
							{mediaUrl ? "Uploaded" : "Pending"}
						</Badge>,
						<Box>
							{mediaUrl ? (
								<Inline gap="4" justifyContent="end" alignItems="center">
									<Button
										level="secondary"
										status="primary"
										as={XHRLink}
										href={`/tourists/${tourist.id}/documents/${documentId}/download`}
										download
										target="_blank"
										title={`Download ${name}`}
									>
										<Icons.DocumentDownload /> Download
									</Button>
									<Dropdown alignRight>
										<Dropdown.ToggleButton level="tertiary" size="sm">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<TouristDocumentUploader
												docName={name}
												mediaUrl={mediaUrl}
												docuemntTypeId={documentTypeId}
												documentId={documentId}
												touristId={tourist.id}
												revalidate={revalidate}
											>
												{({ open }) => (
													<Dropdown.MenuItem onClick={open}>
														<Icons.Upload /> Replace
													</Dropdown.MenuItem>
												)}
											</TouristDocumentUploader>
											<Divider marginY="0" />
											<Dropdown.MenuItem
												color="danger"
												onClick={() => {
													if (
														globalThis.window.confirm(
															`Are you sure you want to delete the document "${name}"? This action cannot be undone.`
														) &&
														documentId
													) {
														removeFile({
															xhr,
															touristId: tourist.id,
															documentId,
															url: mediaUrl,
														}).then(() => {
															revalidate()
														})
														showSnackbar("File deleted successfully!")
													}
												}}
											>
												<Icons.Trash /> Delete
											</Dropdown.MenuItem>
										</Dropdown.Menu>
									</Dropdown>
								</Inline>
							) : (
								<Inline gap="4" justifyContent="end" alignItems="center">
									<TouristDocumentUploader
										docName={name}
										docuemntTypeId={documentTypeId}
										documentId={documentId}
										touristId={tourist.id}
										revalidate={revalidate}
									>
										{({ open }) => (
											<Button
												level="secondary"
												title={`Upload ${name}`}
												onClick={open}
											>
												<Icons.Upload /> Upload
											</Button>
										)}
									</TouristDocumentUploader>
									<Button level="tertiary" size="sm" disabled>
										<Icons.DotsVertical />
									</Button>
								</Inline>
							)}
						</Box>,
					]
				)}
			/>
		</Box>
	)
}

export function EmptyState({
	searched,
	onRefresh,
	isRefreshing,
}: {
	searched?: boolean
	onRefresh?: () => void
	isRefreshing?: boolean
}) {
	return (
		<Box paddingY="16">
			<Stack gap="6" alignItems="center">
				<Icons.Info size="12" color="muted" />
				<Heading as="h3" fontSize="lg" fontWeight="normal">
					{searched
						? "No results matched your search."
						: "There are no documents in this list."}
				</Heading>
				{onRefresh ? (
					<Box>
						<Button onClick={onRefresh}>
							<Icons.Refresh />{" "}
							{isRefreshing ? "Please wait..." : "Refresh Results"}
						</Button>
					</Box>
				) : null}
			</Stack>
		</Box>
	)
}

function AllTouristsPage({ trip }: { trip: ITrip }) {
	const { tourists, mutate, isFetching, isLoading, isValidating } = useTourist({
		tripId: trip.id,
		includes: ["documents_status"],
	})
	return (
		<>
			<ActionLinks
				trip={trip}
				tourists={tourists}
				onRefresh={() => {
					mutate()
				}}
				isLoading={isLoading || isFetching || isValidating}
			/>
			{isLoading || isFetching || isValidating ? (
				<Inline justifyContent="center" alignItems="center" padding="4">
					<Spinner />
				</Inline>
			) : (
				<Stack gap="6" padding="4" paddingTop="6">
					<AllTouristDocumentList tourists={tourists} />
				</Stack>
			)}
		</>
	)
}

function ActionLinks({
	tourists,
	onRefresh,
	isLoading,
	trip,
}: {
	trip: ITrip
	onRefresh: () => void
	isLoading: boolean
	tourists?: TTourist[]
}) {
	const xhr = useXHR()
	const hasIncompleteDocuments =
		!tourists ||
		tourists.length === 0 ||
		tourists.some(
			(tourist) =>
				(tourist.document_tracking?.total_required ?? 0) > 0 &&
				(tourist.document_tracking?.completed_count ?? 0) === 0
		)
	const hasPrimaryTourist = !!tourists?.some((tourist) => tourist.is_primary)
	return (
		<Box>
			<Inline justifyContent="end">
				<Inline
					gap="4"
					borderWidth="1"
					borderColor="muted"
					borderTopColor="transparent"
					paddingX="4"
					paddingY="2"
					borderBottomLeftRadius="md"
					flexWrap={"wrap"}
				>
					<Button inline onClick={onRefresh}>
						<Icons.Refresh spin={isLoading} />
					</Button>
					{hasPrimaryTourist ? (
						<CopyToClipboard>
							{({ copyTextToClipboard }) => (
								<Box display="flex" alignItems="center">
									<CopyToClipboardButton
										onClick={async () => {
											const { link } = await genrateGuestFormLink(xhr, trip.id)
											await copyTextToClipboard(link)
											return true
										}}
										inline
									>
										<Button>
											<Icons.ClipboardCopy /> Request Documents
										</Button>
									</CopyToClipboardButton>
								</Box>
							)}
						</CopyToClipboard>
					) : null}
					{hasIncompleteDocuments ? (
						<Button
							key="no-documents-download"
							level="secondary"
							status="primary"
							title={
								"No documents available for download. At least one document must be uploaded for each guest."
							}
							disabled={hasIncompleteDocuments}
						>
							<Icons.DocumentDownload /> Download
						</Button>
					) : (
						<Button
							as={XHRLink}
							href={`/trips/${trip.id}/tourists/documents/bulk-download`}
							download
							target="_blank"
							status="primary"
							level="secondary"
							title="Download All Guest Documents"
						>
							<Icons.DocumentDownload /> Download
						</Button>
					)}
				</Inline>
			</Inline>
		</Box>
	)
}
function AllTouristDocumentList({ tourists }: { tourists?: TTourist[] }) {
	if (!tourists || tourists?.length === 0) return <EmptyState />
	return (
		<Table
			bordered
			responsive
			striped
			headers={[
				"Name",
				"Contact Details",
				"Pending Documents",
				"Document Status",
				"",
			]}
			rows={(tourists ?? []).map(
				({
					id,
					name,
					age_during_trip,
					nationality,
					phone_numbers,
					email,
					document_tracking,
				}) => {
					const pendingList =
						(document_tracking?.pending_list ?? []).length > 0
							? document_tracking?.pending_list
							: []

					const hasNoDocuments = (document_tracking?.completed_count ?? 0) === 0

					return [
						<Stack key={id}>
							<Text>{name}</Text>
							<Inline>
								{joinAttributes(
									age_during_trip ? <Text>Age: {age_during_trip}</Text> : null,
									nationality ? <Text>{nationality.nationality}</Text> : null
								)}
							</Inline>
						</Stack>,
						<Stack key={id}>
							<PhoneNumber value={phone_numbers} />
							<Email value={email} />
						</Stack>,
						<Box key={id}>
							{(pendingList ?? []).length > 0
								? pendingList?.map((pl) => pl.name).join(", ")
								: "-"}
						</Box>,
						<Badge
							key={id}
							success={
								document_tracking?.total_required
									? document_tracking.is_document_complete
									: false
							}
							warning={
								document_tracking?.total_required
									? !document_tracking.is_document_complete
									: false
							}
							danger={!document_tracking?.total_required}
						>
							{document_tracking?.total_required
								? document_tracking.is_document_complete
									? "Completed"
									: "Pending"
								: "No Documents Added"}
						</Badge>,
						hasNoDocuments ? (
							<Button
								key={id}
								level="secondary"
								title={`No documents available for download for ${name}`}
								disabled={hasNoDocuments}
							>
								<Icons.DocumentDownload /> Download
							</Button>
						) : (
							<Button
								key={id}
								level="secondary"
								as={XHRLink}
								href={`/tourists/${id}/download-zip`}
								download
								target="_blank"
								title={`Download all document of ${name}`}
							>
								<Icons.DocumentDownload /> Download
							</Button>
						),
					]
				}
			)}
		/>
	)
}

type UploadDocumentType = {
	url?: string
	document_file?: File
	removed_file?: string
}

async function removeFile({
	xhr,
	documentId,
	touristId,
	url,
}: {
	xhr: XHRInstance
	touristId: number
	documentId: number
	url: string
}) {
	await TouristXHR(xhr).deleteTouristDocument({ touristId, documentId, url })
}

function TouristDocumentUploader({
	children,
	docName,
	mediaUrl,
	touristId,
	docuemntTypeId,
	documentId,
	revalidate,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	docName: string
	mediaUrl?: string
	docuemntTypeId: number
	documentId?: number
	touristId: number
	revalidate: () => void
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()

	async function triggerFileInput(url: string) {
		setTimeout(() => {
			const input = document.getElementById(
				`file-input-${docName}`
			) as HTMLInputElement | null
			if (input) input.click()
		}, 0)
	}

	const initialValues: UploadDocumentType = {
		url: mediaUrl,
		document_file: undefined,
		removed_file: undefined,
	}

	return (
		<>
			{children({ open: () => openDialog() })}
			<Dialog
				sm
				open={isOpen}
				onClose={() => {
					closeDialog()
					revalidate()
				}}
			>
				<Form<UploadDocumentType>
					validate={validateFormValues(
						Validator.object().shape({
							document_file: Validator.mixed().required(
								`Please select a ${docName} image to upload.`
							),
						})
					)}
					initialValues={
						mediaUrl
							? initialValues
							: {
									url: undefined,
									document_file: undefined,
									removed_file: undefined,
								}
					}
					onSubmit={withServerErrors(async (values) => {
						if (documentId && values.removed_file) {
							await removeFile({
								xhr,
								touristId,
								documentId,
								url: values.removed_file,
							})
						}
						if (values.document_file) {
							await TouristXHR(xhr).uploadDocumentByDocumentId(
								touristId,
								docuemntTypeId,
								values.document_file
							)
							revalidate()
						} else {
							alert(
								`Please select a valid file to upload as the ${docName}. The document file is required.`
							)
						}
						closeDialog()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, form, submitting }) => (
						<form
							noValidate
							onSubmit={handleSubmit}
							encType="multipart/form-data"
						>
							<GetFieldValue<File> name="document_file">
								{({ value: file }) => (
									<GetFieldValue<string> name="url">
										{({ value: url }) => (
											<>
												<Dialog.Header>
													<Dialog.Title>
														<Inline gap="4" alignItems="center">
															<Text>Upload {docName}</Text>
														</Inline>
													</Dialog.Title>
												</Dialog.Header>
												<Dialog.Body>
													{file || url ? (
														<Card bgColor="subtle">
															<Stack
																gap="2"
																paddingX="4"
																paddingTop="2"
																paddingBottom="4"
															>
																<Inline
																	justifyContent="between"
																	alignItems="center"
																	gap="4"
																>
																	<FileInfo file={file} url={url} />
																	<Inline gap="4">
																		<Button
																			level="secondary"
																			status="primary"
																			onClick={() => {
																				form.change("removed_file", url)
																				form.change("url", undefined)
																				form.change("document_file", undefined)
																				triggerFileInput(url)
																			}}
																		>
																			<Icons.Upload />{" "}
																			{file || url ? "Update" : "Upload"}
																		</Button>
																		<Button
																			inline
																			onClick={() => {
																				form.change("removed_file", url)
																				form.change("url", undefined)
																				form.change("document_file", undefined)
																			}}
																		>
																			<Icons.Cancel />
																		</Button>
																	</Inline>
																</Inline>
																<FilePreview url={url} file={file} />
															</Stack>
														</Card>
													) : (
														<DocumentFileInputField
															id={`file-input-${docName}`}
															name="document_file"
														/>
													)}
													<SubmissionError />
												</Dialog.Body>
												<Dialog.Footer>
													<Button type="submit" disabled={submitting}>
														{submitting ? "Saving..." : "Save"}
													</Button>
													<Button
														onClick={() => {
															closeDialog()
															revalidate()
														}}
														level="tertiary"
														disabled={submitting}
													>
														Close
													</Button>
												</Dialog.Footer>
											</>
										)}
									</GetFieldValue>
								)}
							</GetFieldValue>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}
